import PropTypes from 'prop-types';
import React from 'react';
import {get, isFunction} from 'lodash';
import ModuleTitle from './module_title';
import MediaQuery from '../common/media_query';
import withViewportScrollTracking from '../hocs/withViewportScrollTracking';
import DesktopContentModule from './desktop_content_module';
import TabletContentModule from './tablet_content_module';
import MobileContentModule, {
  MODULE_TYPE_HERO,
  MODULE_TYPE_SERIES,
  MODULE_TYPE_SHOPS_CATEGORY,
  MODULE_TYPE_HP_REFRESH_TRENDING_VIDEOS,
  MODULE_TYPE_HP_REFRESH_SHOPS_CATEGORY
} from './mobile_content_module';
import classNames from 'classnames';
import {
  PRODUCT_DETAIL_PAGE_TYPE,
  PRODUCT_LIST_PAGE_TYPE,
  ROW_TYPE_THREE_CHANNELS
} from '../../constants';
import { onHomepage } from '../../utils/dom';

const labelForHomepage = props => {
  return `Reached Homepage Module - ${props.title} - ${props.type}`;
};

/**
 * Asserts that the blog series module contains rows.
 *
 * @param  {string} rows Provided array of module rows.
 * @return {Boolean}
 */
const blogSeriesModuleIsValid = (rows: Array): Boolean => rows.length > 0;

const getScrollDepthEventLabel = (props, context) => (
  {
    'related-content': () => 'Related Content',
    'all-shows': () => 'All Shows',
    'recommended-shows': () => 'Recommended Shows',
    'related_products': () => `Related Products ${props.title}`,
    'shops_category': () => 'Reached Shopping',
    'hp_refresh_most_popular': () => 'Reached Most Popular',
    'hp_refresh_featured_story': () => 'Reached Featured Story',
    'hp_refresh_shops_category': () => 'Reached Shopping',
    'hp_refresh_original_series': () => 'Reached Series module',
    'hp_refresh_feed': () => props.title ? `Reached Homepage Module - ${props.title} - feed` : null,
    'blog_series': () =>
      blogSeriesModuleIsValid(get(props, 'rows', []))
      && 'Reached Series module',
    // Feed, content, and hero modules are only tracked on homepage
    feed: () =>
      onHomepage(context.location, context.edition_data)
      && labelForHomepage(props),
    content: () =>
      onHomepage(context.location, context.edition_data)
      && labelForHomepage(props),
    hero: () =>
      onHomepage(context.location, context.edition_data)
      && labelForHomepage(props)
  }[props.type] || (() => null)
);

const ROW_TYPE_BLACKLIST = [
  ROW_TYPE_THREE_CHANNELS
];

export const ContentModule = (props, { location, edition_data }) => {
  const { rows, type, title, page_type, shouldDisplayModuleTitle } = props;

  const blacklistedRows = rows.filter(
    row => ROW_TYPE_BLACKLIST.includes(row.type)
  );

  if (blacklistedRows.length) {
    return null;
  }

  const largeTitle = () => {
    if (type === MODULE_TYPE_SERIES
      && onHomepage(location, edition_data)
    ) {
      return 'Watch';
    }

    if (type === MODULE_TYPE_SHOPS_CATEGORY
      && ![PRODUCT_DETAIL_PAGE_TYPE, PRODUCT_LIST_PAGE_TYPE].includes(page_type)) {
      return 'Shopping';
    }

    return null;
  };

  return (
    <div className={classNames('module', {
      'hero': type === MODULE_TYPE_HERO,
      'shopping-module': type === MODULE_TYPE_SHOPS_CATEGORY,
      'series-module': type === MODULE_TYPE_SERIES,
      'grey-module': [MODULE_TYPE_SERIES, MODULE_TYPE_HP_REFRESH_SHOPS_CATEGORY, MODULE_TYPE_HP_REFRESH_TRENDING_VIDEOS].includes(type)
    })}
    >
      {shouldDisplayModuleTitle &&
        <ModuleTitle
          title={title}
          largeTitle={largeTitle()}
        />}
      <MediaQuery breakpoint="desktop">
        <DesktopContentModule {...props}
          breakpoint="desktop"
        />
      </MediaQuery>
      <MediaQuery breakpoint="tablet">
        <TabletContentModule {...props}
          breakpoint="tablet"
        />
      </MediaQuery>
      <MediaQuery breakpoint="mobile">
        <MobileContentModule {...props} />
      </MediaQuery>
    </div>
  );
};

ContentModule.propTypes = {
  adsState: PropTypes.object.isRequired,
  archivesState: PropTypes.object,
  rows: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    rowID: PropTypes.number,
    indexWithinModule: PropTypes.number,
    cards: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number }))
  })).isRequired,
  shouldDisplayModuleTitle: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.string.isRequired,
  page_id: PropTypes.number,
  page_type: PropTypes.string
};

ContentModule.defaultProps = {
  adsState: {},
  rows: []
};

ContentModule.contextTypes = {
  location: PropTypes.object,
  edition_data: PropTypes.object
};

const decorator = withViewportScrollTracking({
  eventParams() {
    return [
      'Scroll Depth',
      'Scroll',
      getScrollDepthEventLabel(this.props, this.context)()
    ];
  },
  shouldTrackScrollEvent() {
    const scrollDepthEventLabel = getScrollDepthEventLabel(
      this.props, this.context
    );
    return isFunction(scrollDepthEventLabel) && !!scrollDepthEventLabel();
  }
});

export default decorator(ContentModule);
